import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  FirstName,
  LastName,
  MiddleName,
  PrimaryToast,
  SaveIcon,
  StyledButton,
  phoneNumberMasker,
} from "react-common-ui-library";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  CONSUMER_USER,
  CONTAINED,
  H1,
  H3,
  MODE_ALL,
  P_REGULAR,
  P_REGULAR_SEMI_BOLD,
  SECONDARY_MAIN,
  SPAN,
  SUBMIT,
  SUBTITLE,
  TEXT_SECONDARY,
} from "../../../Constants/CommonConstants";
import {
  ACCOUNT_SETTING_LABEL,
  EMAIL_LABEL,
  EMAIL_VALUE,
  NAME_FIRST_NAME,
  NAME_LAST_NAME,
  NAME_MIDDLE_NAME,
  PERSONAL_DETAILS_HEADING,
  PERSONAL_DETAILS_TEXT,
  PERSONAL_DETAILS_TOAST,
  PHONE_LABEL,
  PHONE_VALUE,
  SAVE_CHANGES_BUTTON,
} from "../../../Constants/MyProfileConstants";
import {
  SLICE_MY_PROFILE,
  THUNK_PUT_NAME,
} from "../../../Constants/SliceAndThunkConstants";
import {
  clearMyProfileApiResponse,
  putName,
} from "../../../Features/MyProfileSlice";
import { manageApiResponseSelector } from "../../../Selectors/ApiResponseSelector";
import { manageAuthorizationSelector } from "../../../Selectors/AuthorizationSelector";
import { manageMyProfileSelector } from "../../../Selectors/MyProfileSelector";
import { getDynamicText } from "../../../Services/CommonServices";
import { StyledPersonalDetailsStack } from "../../../Styles/MyProfile/MyProfile";

/**
 * @description Personal Details screen allows the user to view the personal details and allows them to edit it
 * @returns {ReactNode} Renders the first name, last name, email and phone number with save button
 */
export const PersonalDetails = () => {
  const intl = useIntl();

  const dispatch = useDispatch();

  const [showSaveToast, setShowSaveToast] = useState(false);

  const { scopes } = useSelector((state) => manageAuthorizationSelector(state));

  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    middleName,
    applicationStatus,
  } = useSelector((state) => manageMyProfileSelector(state));

  const methods = useForm({
    mode: MODE_ALL,
  });

  const handleSubmit = (data) => {
    dispatch(putName(data));
  };

  const { apiResponses } = useSelector((state) =>
    manageApiResponseSelector(state, SLICE_MY_PROFILE, THUNK_PUT_NAME)
  );

  const { isSuccess } = apiResponses;

  useEffect(() => {
    if (isSuccess) {
      setShowSaveToast(true);
    }
    dispatch(clearMyProfileApiResponse(THUNK_PUT_NAME));
  }, [dispatch, isSuccess]);

  const handleToastClose = () => {
    setShowSaveToast(false);
  };

  useEffect(() => {
    methods.reset({
      [NAME_FIRST_NAME]: firstName,
      [NAME_MIDDLE_NAME]: middleName,
      [NAME_LAST_NAME]: lastName,
    });
  }, [firstName, lastName, methods, middleName]);

  return (
    <Stack spacing={1.4}>
      <Typography
        color={TEXT_SECONDARY}
        id={ACCOUNT_SETTING_LABEL}
        variant={SUBTITLE}
      >
        {intl.formatMessage({ id: ACCOUNT_SETTING_LABEL })}
      </Typography>
      <Stack spacing={{ xs: 3, md: 4 }}>
        <Typography
          component={H1}
          variant={H3}
          color={SECONDARY_MAIN}
          id={PERSONAL_DETAILS_HEADING}
        >
          {intl.formatMessage({ id: PERSONAL_DETAILS_HEADING })}
        </Typography>
        <Stack spacing={4}>
          {scopes === CONSUMER_USER ? (
            <Typography variant={P_REGULAR} id={PERSONAL_DETAILS_TEXT}>
              {intl.formatMessage(
                { id: PERSONAL_DETAILS_TEXT },
                {
                  consumerText: (
                    <Box component={SPAN}>
                      {intl.formatMessage({
                        id: getDynamicText(applicationStatus),
                      })}
                    </Box>
                  ),
                }
              )}
            </Typography>
          ) : null}
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <StyledPersonalDetailsStack spacing={{ xs: 4, md: 5 }}>
                <FirstName />
                {scopes !== CONSUMER_USER ? <MiddleName /> : null}
                <LastName />
                <Stack spacing={{ xs: 8, md: 10 }}>
                  <Stack spacing={{ xs: 4, md: 5 }}>
                    {phoneNumber ? (
                      <Stack spacing={1.4}>
                        <Typography
                          variant={P_REGULAR_SEMI_BOLD}
                          id={PHONE_LABEL}
                        >
                          {intl.formatMessage({ id: PHONE_LABEL })}
                        </Typography>
                        <Typography variant={P_REGULAR} id={PHONE_VALUE}>
                          {phoneNumberMasker(phoneNumber)}
                        </Typography>
                      </Stack>
                    ) : null}
                    {email ? (
                      <Stack spacing={1.4}>
                        <Typography
                          variant={P_REGULAR_SEMI_BOLD}
                          id={EMAIL_LABEL}
                        >
                          {intl.formatMessage({ id: EMAIL_LABEL })}
                        </Typography>
                        <Typography variant={P_REGULAR} id={EMAIL_VALUE}>
                          {email}
                        </Typography>
                      </Stack>
                    ) : null}
                  </Stack>

                  <StyledButton
                    startIcon={<SaveIcon />}
                    variant={CONTAINED}
                    type={SUBMIT}
                    id={SAVE_CHANGES_BUTTON}
                  >
                    {intl.formatMessage({ id: SAVE_CHANGES_BUTTON })}
                  </StyledButton>
                </Stack>
              </StyledPersonalDetailsStack>
            </form>
          </FormProvider>
        </Stack>
      </Stack>
      <PrimaryToast
        id={PERSONAL_DETAILS_TOAST}
        isOpen={showSaveToast}
        setIsOpen={() => handleToastClose()}
      >
        {intl.formatMessage({ id: PERSONAL_DETAILS_TOAST })}
      </PrimaryToast>
    </Stack>
  );
};

export default PersonalDetails;
